import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid2,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
import { ArrowOutward } from "@mui/icons-material";
import { IoIosHappy } from "react-icons/io";
import { MdOutlineArrowOutward } from "react-icons/md";
import { useGetIssuesKPIQuery } from "../../../store/slices/apis/issuesDashboardSlice";
import IssuesPopup from "./IssuesPopup";
import { useParams } from "react-router-dom";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import ProjectStatusOverviewHead from "components/ProjectStatusOverviewHead";
import { ReactComponent as ArrowIcon } from "assets/PwC_Funct_Icons_ArrowIncrease_Outline_Black_RGB.svg"
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"

const IssuesKPI = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { data: IssuesKPI = [], isLoading } = useGetIssuesKPIQuery({
    projectId: projectId,
  });
  const kpiRaw = IssuesKPI?.data || {};

  const lastUpdated = IssuesKPI?.data?.lastUpdated;

  const kpiArr = [
    {
      title: "Open Issues",
      value: kpiRaw?.openIssueCount,
      change: kpiRaw?.openIssueGrowth,
      color: "#9ED3CC",
      changeColor: "#474747",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      status: "Open",
      timeFrame: "this month",
    },
    {
      title: "Total Critical Issues",
      value: kpiRaw?.criticalIssueCount,
      change: kpiRaw?.criticalIssueGrowth,
      color: "#9ED3CC",
      changeColor: "#474747",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      status: "TotalCritical",
      timeFrame: "this month",
    },
    {
      title: "Issues Logged this month",
      value: kpiRaw?.loggedThisMonth,
      change: kpiRaw?.loggedGrowth,
      color: "#9ED3CC",
      changeColor: "#474747",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      status: "Issues",
      timeFrame: "this month",
    },
    {
      title: "Actionable issues this week",
      value: kpiRaw?.actionableThisWeek,
      change: kpiRaw?.actionableThisWeekGrowth,
      color: "#9ED3CC",
      changeColor: "#474747",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      status: "Actionable",
      timeFrame: "this month",
    },
  ];

  const kpiData = kpiArr?.map((item) => {
    const changeValue = item.change;
    const arrow = changeValue >= 0 ? "up" : "down";
    const isPositive = changeValue > 0 ? true : false;
    const absoluteChange = isNaN(changeValue)
      ? "-"
      : changeValue === -100
        ? "0%"
        : `${Math.abs(changeValue)}%`;
    const redColor = "#CE412D";
    return {
      ...item,
      arrow,
      absoluteChange,
      isPositive,
      redColor,
    };
  });

  if (isLoading) {
    return (
      <>
        <Grid2 container spacing={5} sx={{ mt: "20px" }}>
          <ProjectStatusOverviewHead
            lastModified={lastUpdated}
            marginBottm={"10px"}
          />
        </Grid2>
        <Grid2 container spacing={5} sx={{ mt: "20px" }}>
          {Array(4)
            .fill({})
            .map((_, index) => (
              <Grid2 key={index} size={{ xs: 12, md: 3 }}>
                <Card
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #25252517",
                    borderRadius: "3px",
                  }}
                >
                  <Box display="flex" alignItems="center" p={2}>
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ mr: 2, borderRadius: 1 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="text" width="30%" height={20} />
                      </Box>
                      <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                  </Box>
                </Card>
              </Grid2>
            ))}
        </Grid2>
      </>
    );
  }

  return (
    <>
      <Grid2 container rowSpacing={1.25} columnSpacing={2} sx={{ mt: "20px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"20px"}
        />

        {kpiData.map((card, index) => (
          <Grid2 key={index} size={{ xs: 12, md: 3, lg: 3 }}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pl: "20px", pr: "20px", pt: "17px", pb: "17px" }}
              >
                <Box display="flex" alignItems="center" sx={{ mr: 0 }}>
                  <Typography
                    sx={{
                      bgcolor: card.color,
                      width: "45px",
                      height: "45px",
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: "18px",
                          lineHeight: "100%",
                          mr: "6px",
                          color: card.valueColor,
                          letterSpacing: "0.4px",
                        }}
                      >
                        {card.value}
                      </Typography>
                      <Chip
                        label={card.absoluteChange}
                        size="small"
                        sx={{
                          backgroundColor: "#E6F4EA",
                          color: card?.isPositive
                            ? card?.redColor
                            : card.changeColor,
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                        icon={
                          card?.absoluteChange !== "0%" &&
                          // (card.arrow === "up" ? (
                          //   <ArrowUpward
                          //     sx={{
                          //       fontSize: "12px !important",
                          //       color: !card.isPositive
                          //         ? `${card.changeColor} !important`
                          //         : `${card?.redColor} !important`,
                          //     }}
                          //   />
                          // ) : (
                          //   <ArrowDownward
                          //     sx={{
                          //       fontSize: "12px !important",
                          //       color: !card.isPositive
                          //         ? `${card.changeColor} !important`
                          //         : `${card.redColor} !important`,
                          //     }}
                          //   />
                          // ))
                          (
                            card.arrow === "up" ? (
                              <ArrowUpIcon
                                style={{
                                  height: "12px",
                                  width: "12px",
                                  fill: !card.isPositive ? card.changeColor : card.redColor,
                                }}
                              />
                            ) : (
                              <ArrowDownIcon
                                style={{
                                  height: "12px",
                                  width: "12px",
                                  fill: !card.isPositive ? card.changeColor : card.redColor,
                                }}
                              />
                            )
                          )

                        }
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          ml: "2px",
                          fontWeight: 400,
                          fontSize: "13px",
                          lineHeight: "24px",
                          letterSpacing: "0.4px",
                          color: "#474747",
                        }}
                      >
                        {card.timeFrame}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "13px",
                        lineHeight: "100%",
                        color: "#252525",
                        mt: "5px",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  {card.status && (
                    <Avatar
                      sx={{
                        bgcolor: "#fff",
                        width: "37px",
                        height: "37px",
                        border: "1px solid #d3d3d3",
                      }}
                    >
                      {/* <MdOutlineArrowOutward
                                             style={{
                                               color: "#252525",
                                               fontSize: "20px",
                                               cursor: "pointer",
                                              
                                             }}
                                             onClick={() => {
                                               setPopupData(card);
                                               setOpen(true);
                                             }}
                                           /> */}
                      <ArrowIcon
                        style={{
                          color: "#252525",
                          fontSize: "20px",
                          cursor: "pointer",
                          height: "13px",
                          stroke: "#252525",
                          strokeWidth: "6"
                        }}
                        onClick={() => {
                          setPopupData(card);
                          setOpen(true);
                        }}
                      />
                    </Avatar>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
      {open && (
        <IssuesPopup
          title={popupData.title}
          value={popupData.value}
          status={popupData.status}
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default IssuesKPI;
